import type { SheetGeometryCategoryTable } from "building-types/views/sheetGeometryCategory";
import { SheetGeometryCategory } from "building-types/views/sheetGeometryCategory";
import type {
  SharedGradientDescentRelaxationConstraintType,
  SheetGeometry,
  SheetPolylineGeometry,
} from "building-types/views/sheetGeometryTypes";
import {
  sheetPolylineGeometryNew,
  SheetGeometryType,
  sheetTextGeometryNew,
  GRADIENT_DESCENT_NO_CONSTRAINT,
} from "building-types/views/sheetGeometryTypes";
import { BUILT_IN_VIEW_SCALE_1_PER_4_INCH } from "building-types/views/viewScaleConstants";
import colors from "common/constants/colorsOld";
import { SVG_UNITS_TO_INCHES } from "common/constants/pdfSettings";
import type { RWTable, Table } from "common/data/table";
import { sheetInchesNew } from "common/utils/nominalNumbers";
import { docEntityHandleHash } from "doc/entity/core/handleHash";
import { LW_THIN } from "doc/plans/constants";
import { buildLeader } from "doc/plans/planAnnotations";
import { dimLineworkCategory, dimTickLineworkCategory, dimWitnessCategory } from "doc/plans/planDimensions";
import type { PlanGeometryRelaxer, AbstractPlanGeometrySource } from "doc/plans/planGeometrySource";
import { PlanGeometryBuilder } from "doc/plans/planGeometrySource";
import type {
  TaggedPlanGeometry,
  CategorizedPolylineGeometry,
  CategorizedTextGeometry,
  CategorizedSVGGeometry,
} from "doc/plans/planGeometrySourceTypes";
import type { RelaxationObstacle } from "doc/plans/planGeometrySourceTypes";
import { sheetGeometryScaleStyle } from "doc/plans/primitives/primitiveStyles";
import { planTextGeometryOBB, textGeometryOrigin } from "doc/plans/primitives/textPrimitive";
import { sheetGeometryOBB2, sheetGeometryAABB2 } from "doc/plans/sheetGeometryAABB2";
import { hasLazyRelaxationObstacleObbs } from "doc/plans/sheetGeometryEffects";
import { evalSheetGeometrySelector } from "doc/ruleBasedSelectorTools";
import { linearDimensionToPolyLine2sWithTicks } from "doc/utils/annotation";
import {
  aabb2Center,
  aabb2XOverlap,
  aabb2YOverlap,
  aabb2Union,
  aabb2ByArray,
  aabb2UnionAll,
  aabb2Gap,
  aabb2ContainsVec2,
} from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import { DIST_EPSILON, DIST_EPSILON_SQUARED } from "geom/core";
import { line2Normal, line2ProjectPt, type Line2Segment } from "geom/line2";
import { line2ClipToAABB } from "geom/line2ClipToAABB";
import {
  aabb2FromOBB2,
  obb2ByTwoPoints,
  obb2CollisionDetect,
  obb2Copy,
  obb2FromAABB2,
  obb2GetAxes,
  obb2Grow,
  obb2HasNaN,
  obb2IsAxisAligned,
  obb2Move,
  obb2ScaleAroundPoint,
  polyLine2FromOBB2,
  type OBB2,
} from "geom/obb2";
import {
  polyLine2AABB2,
  polyLine2MoveCopy,
  polyLine2Start,
  polyLine2End,
  polyLine2IsHorizontalEdge,
  polyLine2IsVerticalEdge,
} from "geom/polyLine2";
import { ray2DotProject } from "geom/ray2";
import type { Vec2 } from "geom/vec2";
import { vec2Sub, vec2Len, vec2Copy, vec2Normalize, vec2ProjectScaleFactor, vec2Dot } from "geom/vec2";
import RBush from "rbush";

const SAMPLING_OFFSET = 0.001;
// Minimum visual gap (sheet inches, so it means the same on paper at any scale) before a leader is
// drawn. aabb2Gap is a true edge-to-edge distance, so the 0.5 world-inch it was tuned at is used
// as-is - the old aabb2Overlaps grew both boxes and so demanded 2x.
const ANCHOR_LEADER_GAP_THRESHOLD_SHEET_INCHES = 0.5 * BUILT_IN_VIEW_SCALE_1_PER_4_INCH;
const REPORT_INVALID_COST_COMPONENTS = process.env.NODE_ENV === "development";

export function createGradientDescentRelaxer(
  interactionMap: SheetGeometryCategoryTable<RelaxationObstacle[]>,
  weightMap: SheetGeometryCategoryTable<number>,
  textScale: number,
  viewScale: number,
  viewRotationRads: number = 0,
  minEffectIterationStep: number | undefined = 0.2,
  maxEffectIterationDistance: number | undefined = 6,
): PlanGeometryRelaxer {
  return async (category: SheetGeometryCategory, source: AbstractPlanGeometrySource) => {
    const builder = new PlanGeometryBuilder();

    if (interactionMap[category] === undefined) {
      return { [category]: await source.getPlanGeometry(category) };
    }
    const gdResult = await relaxPlanGeometrySource(
      source,
      interactionMap,
      weightMap,
      textScale,
      viewScale,
      viewRotationRads,
      minEffectIterationStep,
      maxEffectIterationDistance,
    );
    // DEBUG
    for (const result of gdResult.intermediateStatuses) {
      if (!(result.obbs !== undefined)) {
        console.assert(false);
      }
      if (!(result.entity.category !== undefined)) {
        console.assert(false);
      }
      for (const obb of result.obbs) {
        builder.addGeometry(
          sheetPolylineGeometryNew({
            paths: [polyLine2FromOBB2(obb)],
            linewidth: LW_THIN,
            strokeColor: result.color,
            fill: false,
          }),
          SheetGeometryCategory.RELAXATION_DEBUG,
        );
      }
    }
    for (const result of gdResult.obbs) {
      if (interactionMap[result.geometry.category] === undefined) {
        continue;
      }
      switch (result.geometry.type) {
        case SheetGeometryType.TEXT: {
          const relaxedTextGeometry = sheetTextGeometryNew({
            ...result.geometry,
            x: result.geometry.x + result.shiftX,
            y: result.geometry.y + result.shiftY,
          });

          // Leaders land on the glyph, not the anchor, so they measure from the offset origin.
          const { x, y } = textGeometryOrigin(relaxedTextGeometry);

          builder.addGeometry(relaxedTextGeometry, result.geometry.category);

          if (result.geometry.anchorPoint) {
            const hasEntityLeaderFields =
              typeof relaxedTextGeometry.buildingEntity?.leaderLineX === "number" &&
              typeof relaxedTextGeometry.buildingEntity?.leaderLineY === "number";

            if (hasEntityLeaderFields) {
              const leaderLineX = relaxedTextGeometry.buildingEntity.leaderLineX;
              const leaderLineY = relaxedTextGeometry.buildingEntity.leaderLineY;
              let landingLineX: number | undefined = undefined;
              let landingLineY: number | undefined = undefined;
              if (
                relaxedTextGeometry.buildingEntity.landingLineX !== undefined &&
                relaxedTextGeometry.buildingEntity.landingLineY !== undefined
              ) {
                if (Math.abs(result.shiftX) < DIST_EPSILON && Math.abs(result.shiftY) < DIST_EPSILON) {
                  landingLineX = relaxedTextGeometry.buildingEntity.landingLineX;
                  landingLineY = relaxedTextGeometry.buildingEntity.landingLineY;
                } else if (Math.abs(x - leaderLineX) > Math.abs(y - leaderLineY)) {
                  landingLineX = x + (leaderLineX - x) * 0.5;
                  landingLineY = y;
                } else {
                  landingLineX = x;
                  landingLineY = y + (leaderLineY - y) * 0.5;
                }
              }

              buildLeader(
                builder,
                {
                  x,
                  y,
                  leaderLineX,
                  leaderLineY,
                  landingLineX,
                  landingLineY,
                },
                relaxedTextGeometry,
                result.geometry.category,
                //TODO: Add color
              );
            } else {
              // Loads share the handle of the *rule* that made them, across every load and controller,
              // and it is optional - so the anchor is the box containing anchorPoint, not any match.
              const labelHandle = result.geometry.handle;
              const anchorPoint = result.geometry.anchorPoint;
              const anchorComponent = labelHandle
                ? gdResult.obbs.find(
                    (obb) =>
                      obb.geometry.handle === labelHandle &&
                      obb.geometry.category !== result.geometry.category &&
                      aabb2ContainsVec2(aabb2FromOBB2(obb.initialOBB2), anchorPoint.x, anchorPoint.y),
                  )
                : undefined;

              // A label with no glyphs to draw must never show a leader, however far its invisible
              // box has drifted from the anchor.
              const leaderDefinition =
                anchorComponent && relaxedTextGeometry.text.trim() !== ""
                  ? buildAnchorPointLeaderDefinition(
                      result.geometry.anchorPoint,
                      aabb2FromOBB2(obb2Move(obb2Copy(result.initialOBB2), result.shiftX, result.shiftY)),
                      aabb2FromOBB2(
                        obb2Move(obb2Copy(anchorComponent.initialOBB2), anchorComponent.shiftX, anchorComponent.shiftY),
                      ),
                      // The OBBs above are world inches, so the sheet-inch threshold converts to mean the
                      // same visual gap at every view scale.
                      ANCHOR_LEADER_GAP_THRESHOLD_SHEET_INCHES / viewScale,
                    )
                  : undefined;

              if (leaderDefinition) {
                // ELECTRIC_LOAD_LABEL has no default `line` style, so pass a color explicitly
                // or the leader renders as an invisible line with only a visible arrowhead.
                const leaderColor = relaxedTextGeometry.fontColor ?? colors.black;
                // No arrowhead for load-label leaders: with load labels this dense/clustered, the
                // arrowheads add visual clutter without adding clarity - the plain line is enough
                buildLeader(
                  builder,
                  leaderDefinition,
                  relaxedTextGeometry,
                  result.geometry.category,
                  leaderColor,
                  false,
                );
              }
            }
          } else if (result.geometry.dimensionProps) {
            const dimensionProps = result.geometry.dimensionProps;
            const { startAnchor, endAnchor, baseDimLine, startExt, endExt, witnessLineGap } = dimensionProps;

            // project the new position onto the dimension line
            const [oldLineStartX, oldLineStartY] = polyLine2Start(baseDimLine);
            const [oldLineEndX, oldLineEndY] = polyLine2End(baseDimLine);
            const [px, py] = line2ProjectPt(oldLineStartX, oldLineStartY, oldLineEndX, oldLineEndY, x, y);
            const [lineOffsetX, lineOffsetY] = vec2Sub(x, y, px, py);

            // create a new dimension line that is offset by the line offset
            const newDimensionLine = polyLine2MoveCopy(baseDimLine, lineOffsetX, lineOffsetY);
            const [newLineStartX, newLineStartY] = polyLine2Start(newDimensionLine);
            const [newLineEndX, newLineEndY] = polyLine2End(newDimensionLine);

            // make a new dimension with the new dimension line
            const {
              lengthEdges: dimPls,
              witnesses,
              ticks,
            } = linearDimensionToPolyLine2sWithTicks({
              startX: startAnchor[0],
              startY: startAnchor[1],
              endX: endAnchor[0],
              endY: endAnchor[1],
              lineStartX: newLineStartX,
              lineStartY: newLineStartY,
              lineEndX: newLineEndX,
              lineEndY: newLineEndY,
              startExt,
              endExt,
              witnessGap: witnessLineGap,
            });

            const lineworkCategory = dimLineworkCategory(dimensionProps);

            const tickCategory = dimTickLineworkCategory(dimensionProps);

            const witnessCategory = dimWitnessCategory(dimensionProps);

            const lineworkGeometry: SheetGeometry = sheetPolylineGeometryNew({
              handle: result.geometry.handle,
              paths: dimPls,
              buildingEntity: result.geometry.buildingEntity,
              groupId: result.geometry.groupId,
            });
            const tickGeometry: SheetGeometry = sheetPolylineGeometryNew({
              handle: result.geometry.handle,
              paths: ticks,
              buildingEntity: result.geometry.buildingEntity,
              groupId: result.geometry.groupId,
            });
            const witnessGeometry: SheetGeometry[] = witnesses.map((witnessPl) =>
              sheetPolylineGeometryNew({
                handle: result.geometry.handle,
                paths: [witnessPl],
                buildingEntity: result.geometry.buildingEntity,
                groupId: result.geometry.groupId,
              }),
            );

            builder.addGeometry(lineworkGeometry, lineworkCategory);

            builder.addGeometry(tickGeometry, tickCategory);

            witnessGeometry.forEach((witness) => builder.addGeometry(witness, witnessCategory));
          }
          break;
        }
        case SheetGeometryType.POLYLINE: {
          builder.addGeometry(
            sheetPolylineGeometryNew({
              ...result.geometry,
              paths: result.geometry.paths.map((pl) => polyLine2MoveCopy(pl, result.shiftX, result.shiftY)),
            }),
            result.geometry.category,
          );
          break;
        }
        default:
      }
    }
    return builder.taggedGeometries;
  };
}

/**
 * Builds a box-clipped leader (leaderLineX/Y + landing point) for a label with only a generic
 * `anchorPoint` (e.g. electrical load labels), once `labelAABB` and `anchorAABB` have visibly
 * separated by more than `gapThresholdInches`. Returns undefined if they haven't separated
 * enough, or if the clip fails.
 *
 * `gapThresholdInches` is in the same space as the AABBs - world inches for the relaxer's boxes.
 */
export function buildAnchorPointLeaderDefinition(
  anchorPoint: { x: number; y: number },
  labelAABB: AABB2,
  anchorAABB: AABB2,
  gapThresholdInches: number,
): { x: number; y: number; leaderLineX: number; leaderLineY: number } | undefined {
  if (aabb2Gap(labelAABB, anchorAABB) < gapThresholdInches) {
    return undefined;
  }

  const [labelCenterX, labelCenterY] = aabb2Center(labelAABB);

  const clippedAtAnchor = line2ClipToAABB(anchorPoint.x, anchorPoint.y, labelCenterX, labelCenterY, anchorAABB);
  const clippedAtLabel = line2ClipToAABB(anchorPoint.x, anchorPoint.y, labelCenterX, labelCenterY, labelAABB);
  if (!clippedAtAnchor || !clippedAtLabel) {
    return undefined;
  }

  const [, , leaderLineX, leaderLineY] = clippedAtAnchor;
  const [, , landingX, landingY] = clippedAtLabel;

  return { x: landingX, y: landingY, leaderLineX, leaderLineY };
}

function getDebugColor(obb: OBB2, isObstacle: boolean): string {
  return isObstacle ? getObstacleDebugColor(obb) : getTargetDebugColor(obb);
}

function getObstacleDebugColor(obb: OBB2): string {
  return obb2IsAxisAligned(obb) ? "green" : "lightgreen";
}

function getTargetDebugColor(obb: OBB2): string {
  return obb2IsAxisAligned(obb) ? "blue" : "cyan";
}

function collectPolylineObstacleCostComponents(
  obstacle: CategorizedPolylineGeometry,
  costComponents: OBB2GradientDescentCostComponent[] = [],
): OBB2GradientDescentCostComponent[] {
  function addCostComponent(obb: OBB2, obstacle: TaggedPlanGeometry) {
    const costComponent = new OBB2GradientDescentCostComponent(obb, getObstacleDebugColor(obb), obstacle);
    costComponents.push(costComponent);
  }

  if (obstacle.oneObstacle) {
    let aabb: AABB2 | undefined = undefined;
    for (const path of obstacle.paths) {
      if (aabb === undefined) {
        aabb = polyLine2AABB2(path);
      } else {
        aabb = aabb2Union(aabb, polyLine2AABB2(path));
      }
    }
    if (aabb) {
      const obb = obb2FromAABB2(aabb);
      addCostComponent(obb, obstacle);
    }
  } else {
    for (let i = 0; i < obstacle.paths.length; i++) {
      const path = obstacle.paths[i];
      // let thickenBy = 0.5;
      // if (obstacle.linewidth) {
      //   thickenBy = Array.isArray(obstacle.linewidth) ? obstacle.linewidth[i] / 2 : obstacle.linewidth / 2;
      // }
      for (let j = 0; j < path.length - 3; j += 2) {
        const segmentStartX = path[j];
        const segmentStartY = path[j + 1];
        const segmentEndX = path[j + 2];
        const segmentEndY = path[j + 3];
        const obb = obb2ByTwoPoints(segmentStartX, segmentStartY, segmentEndX, segmentEndY);

        // obb2Grow(obb, 0, thickenBy);
        addCostComponent(obb, obstacle);
      }
    }
  }

  return costComponents;
}

function collectPolylineTargetCostComponents(
  target: CategorizedPolylineGeometry,
  costComponents: OBB2GradientDescentCostComponent[] = [],
): OBB2GradientDescentCostComponent[] {
  if (target.anchorPoint === undefined && target.dimensionProps === undefined) {
    const aabb = aabb2ByArray(target.paths);
    if (aabb) {
      const obb = obb2FromAABB2(aabb);
      const costComponent = new OBB2GradientDescentCostComponent(obb, getTargetDebugColor(obb), target);

      costComponents.push(costComponent);
    }
  }

  return costComponents;
}

function isDimensionBeingRelaxed(geometry: SheetPolylineGeometry, relaxedGroups: Table<boolean>): boolean {
  return !!geometry.dimensionProps && !!geometry.groupId && relaxedGroups[geometry.groupId];
}

function collectPolylineCostComponents(
  geometry: CategorizedPolylineGeometry,
  relaxedGroups: Table<boolean>,
  isObstacle: boolean,
  costComponents: OBB2GradientDescentCostComponent[] = [],
): OBB2GradientDescentCostComponent[] {
  // skip dimension lines being relaxed since they using multiple OBBs for the label cost component
  if (isDimensionBeingRelaxed(geometry, relaxedGroups)) {
    return costComponents;
  }

  return isObstacle
    ? collectPolylineObstacleCostComponents(geometry, costComponents)
    : collectPolylineTargetCostComponents(geometry, costComponents);
}

function collectTextCostComponents(
  geometry: CategorizedTextGeometry,
  textScale: number,
  viewScale: number,
  viewRotationRads: number,
  isObstacle: boolean,
  costComponents: OBB2GradientDescentCostComponent[] = [],
): OBB2GradientDescentCostComponent[] {
  const textRelaxationGeometry =
    viewRotationRads !== 0 && geometry.rotationBasis === "SHEET"
      ? { ...geometry, rotation: viewRotationRads }
      : geometry;

  const obb = planTextGeometryOBB(sheetGeometryScaleStyle(textRelaxationGeometry, textScale / viewScale));

  const subCostComponents: OBB2GradientDescentSubCostComponent[] = [];
  if (geometry.dimensionProps) {
    const { baseDimLine } = geometry.dimensionProps;

    const dimensionLineOBB = obb2ByTwoPoints(baseDimLine[0], baseDimLine[1], baseDimLine[2], baseDimLine[3]);
    const constraintDirection = vec2Copy(line2Normal(baseDimLine[0], baseDimLine[1], baseDimLine[2], baseDimLine[3]));
    subCostComponents.push({
      obb: dimensionLineOBB,
      tag: dimLineworkCategory(geometry.dimensionProps),
      constraintDirection,
    });
  }

  costComponents.push(
    new OBB2GradientDescentCostComponent(obb, getDebugColor(obb, isObstacle), geometry, subCostComponents),
  );

  return costComponents;
}

function collectSVGCostComponents(
  geometry: CategorizedSVGGeometry,
  viewScale: number,
  isObstacle: boolean,
  costComponents: OBB2GradientDescentCostComponent[] = [],
): OBB2GradientDescentCostComponent[] {
  // SVG units are drawn at SVG_UNITS_TO_INCHES of *sheet* space, so this converts to world inches.
  // (Was BUILT_IN_VIEW_SCALE_1_PER_8_INCH with a "1/8 mystery factor" TODO - never a view scale.)
  const scale = SVG_UNITS_TO_INCHES / viewScale;
  const obb = sheetGeometryOBB2({ ...geometry, inPlanScale: true });
  if (!obb) {
    console.warn(`Unexpected obstacle type ${geometry.type} or degenerate geometry`);
    return costComponents;
  }
  obb2ScaleAroundPoint(obb, scale, scale, geometry.x, geometry.y);

  costComponents.push(new OBB2GradientDescentCostComponent(obb, getDebugColor(obb, isObstacle), geometry));

  return costComponents;
}

function collectDefaultCostComponents(
  geometry: TaggedPlanGeometry,
  isObstacle: boolean,
  costComponents: OBB2GradientDescentCostComponent[] = [],
): OBB2GradientDescentCostComponent[] {
  const obb = sheetGeometryOBB2(geometry);
  if (!obb) {
    console.warn(`Unexpected obstacle type ${geometry.type} or degenerate geometry`);
    return costComponents;
  }

  costComponents.push(new OBB2GradientDescentCostComponent(obb, getDebugColor(obb, isObstacle), geometry));

  return costComponents;
}

export function collectCostComponents(
  geometry: TaggedPlanGeometry,
  textScale: number,
  viewScale: number,
  viewRotationRads: number,
  relaxedGroups: Table<boolean>,
  isObstacle: boolean,
  costComponents: OBB2GradientDescentCostComponent[] = [],
): OBB2GradientDescentCostComponent[] {
  const { relaxationObstacleOBBs = [] } = geometry;
  const addCostComponents = (newCostComponents: OBB2GradientDescentCostComponent[]) => {
    costComponents.push(
      ...newCostComponents.filter((costComponent) =>
        isValidCostComponent(costComponent, REPORT_INVALID_COST_COMPONENTS),
      ),
    );
  };

  // override if needed
  if (relaxationObstacleOBBs.length > 0) {
    addCostComponents(
      relaxationObstacleOBBs.map((obb) => {
        return new OBB2GradientDescentCostComponent(obb, getDebugColor(obb, isObstacle), geometry);
      }),
    );

    return costComponents;
  }

  switch (geometry.type) {
    case SheetGeometryType.POLYLINE: {
      addCostComponents(collectPolylineCostComponents(geometry, relaxedGroups, isObstacle));
      break;
    }
    case SheetGeometryType.TEXT: {
      addCostComponents(collectTextCostComponents(geometry, textScale, viewScale, viewRotationRads, isObstacle));
      break;
    }
    case SheetGeometryType.SVG: {
      addCostComponents(collectSVGCostComponents(geometry, viewScale, isObstacle));
      break;
    }
    default: {
      addCostComponents(collectDefaultCostComponents(geometry, isObstacle));
      break;
    }
  }

  return costComponents;
}

function collectVars(
  constraints: SharedGradientDescentRelaxationConstraintType | undefined,
  interactionMap: SheetGeometryCategoryTable<RelaxationObstacle[]>,
  weightMap: SheetGeometryCategoryTable<number>,
  costComponents: OBB2GradientDescentCostComponent[],
  vars: GradientDescentVariable<AABB2>[] = [],
): GradientDescentVariable<AABB2>[] {
  for (const costComponent of costComponents) {
    const weight = weightMap[costComponent.geometry.category] ?? 1;

    if (constraints) {
      for (const constraint of constraints) {
        if (polyLine2IsHorizontalEdge(constraint, 0)) {
          const xVar = new OBB2GradientDescentLimitedXVariable(
            costComponent,
            weight,
            interactionMap,
            Math.min(constraint[0], constraint[2]),
            Math.max(constraint[0], constraint[2]),
          );
          vars.push(xVar);
        } else if (polyLine2IsVerticalEdge(constraint, 0)) {
          const yVar = new OBB2GradientDescentLimitedYVariable(
            costComponent,
            weight,
            interactionMap,
            Math.min(constraint[1], constraint[3]),
            Math.max(constraint[1], constraint[3]),
          );
          vars.push(yVar);
        } else {
          const linearVar = new OBB2GradientDescentLinearLimitedVariable(costComponent, weight, interactionMap, [
            constraint[0],
            constraint[1],
            constraint[2],
            constraint[3],
          ]);
          vars.push(linearVar);
        }
      }
    } else {
      if (obb2IsAxisAligned(costComponent.initialOBB2)) {
        const xVar = new OBB2GradientDescentLimitedXVariable(
          costComponent,
          weight,
          interactionMap,
          Number.NEGATIVE_INFINITY,
          Number.POSITIVE_INFINITY,
        );
        vars.push(xVar);
        const yVar = new OBB2GradientDescentLimitedYVariable(
          costComponent,
          weight,
          interactionMap,
          Number.NEGATIVE_INFINITY,
          Number.POSITIVE_INFINITY,
        );
        vars.push(yVar);
      } else {
        const { centerX, centerY } = costComponent.initialOBB2;
        const axes = obb2GetAxes(costComponent.initialOBB2);

        const [ux, uy] = axes[0];
        const [vx, vy] = axes[1];

        const range = 10000;
        const halfRange = range / 2;

        const uConstraint: Line2Segment = [
          centerX - ux * halfRange,
          centerY - uy * halfRange,
          centerX + ux * halfRange,
          centerY + uy * halfRange,
        ];
        const vConstraint: Line2Segment = [
          centerX - vx * halfRange,
          centerY - vy * halfRange,
          centerX + vx * halfRange,
          centerY + vy * halfRange,
        ];

        const linearVarU = new OBB2GradientDescentLinearLimitedVariable(
          costComponent,
          weight,
          interactionMap,
          uConstraint,
        );
        const linearVarV = new OBB2GradientDescentLinearLimitedVariable(
          costComponent,
          weight,
          interactionMap,
          vConstraint,
        );

        vars.push(linearVarU);
        vars.push(linearVarV);
      }
    }
  }

  return vars;
}

async function collectRelaxedGroups(
  relaxationTargetCategories: SheetGeometryCategory[],
  source: AbstractPlanGeometrySource,
): Promise<Table<boolean>> {
  const relaxedGroups: RWTable<boolean> = {};

  for (const relaxationTargetTag of relaxationTargetCategories) {
    const relaxationTargets = await source.getPlanGeometry(relaxationTargetTag);
    for (const relaxationTarget of relaxationTargets) {
      if (!relaxationTarget.groupId) {
        continue;
      }

      if (!relaxedGroups[relaxationTarget.groupId]) {
        relaxedGroups[relaxationTarget.groupId] = true;
      }
    }
  }

  return relaxedGroups;
}

function isValidCostComponent(costComponent: OBB2GradientDescentCostComponent, report: boolean = false): boolean {
  if (
    obb2HasNaN(costComponent.initialOBB2) ||
    costComponent.initialSubCostComponents.some((subCostComponent) => obb2HasNaN(subCostComponent.obb))
  ) {
    if (report) {
      const { geometry } = costComponent;
      let warningMsg = `Skipping invalid costComponent from geometry of type "${costComponent.geometry.type}"`;

      if (geometry.category) {
        warningMsg += ` with category "${geometry.category}"`;
      }

      if (geometry.handle) {
        warningMsg += ` and handle "${docEntityHandleHash(geometry.handle)}"`;
      }

      console.warn(warningMsg);
    }

    return false;
  }

  return true;
}

export async function relaxPlanGeometrySource(
  source: AbstractPlanGeometrySource,
  interactionMap: SheetGeometryCategoryTable<RelaxationObstacle[]>,
  weightMap: SheetGeometryCategoryTable<number>,
  textScale: number = 1,
  viewScale: number = BUILT_IN_VIEW_SCALE_1_PER_4_INCH,
  viewRotationRads: number = 0,
  minEffectIterationStep: number | undefined = 0.2,
  maxEffectIterationDistance: number | undefined = 6,
) {
  const relaxationTargetCategories: SheetGeometryCategory[] = Object.keys(interactionMap) as SheetGeometryCategory[];
  const obstacleCategories: SheetGeometryCategory[] = Object.keys(
    Object.values(interactionMap).reduce((valueSet, obstacles) => {
      obstacles.forEach((obstacle) => {
        // relaxation targets are obstacles too, no need to duplicate them here
        if (interactionMap[obstacle.category] === undefined) {
          valueSet[obstacle.category] = true;
        }
      });
      return valueSet;
    }, {}),
  ) as SheetGeometryCategory[];

  const maxIterationDistance = maxEffectIterationDistance ?? 6;
  const iterationStep = minEffectIterationStep ?? 0.2;

  const obbs: OBB2GradientDescentCostComponent[] = [];
  const vars: GradientDescentVariable<AABB2>[] = [];
  const relaxedGroups = await collectRelaxedGroups(relaxationTargetCategories, source);

  const sharedVars: Map<string, OBB2GradientDescentCostComponent[]> = new Map();

  // Get relaxation targets
  for (const relaxationTargetTag of relaxationTargetCategories) {
    const relaxationTargetsWithoutObbs = await source.getPlanGeometry(relaxationTargetTag);
    const relaxationTargets = constructGeometryWithLazyGroupRelaxationObbs(
      relaxationTargetTag,
      textScale / viewScale,
      relaxationTargetsWithoutObbs,
    );

    for (const relaxationTarget of relaxationTargets) {
      const costComponents = collectCostComponents(
        relaxationTarget,
        textScale,
        viewScale,
        viewRotationRads,
        relaxedGroups,
        false,
      );

      if (relaxationTarget.sharedConstraints) {
        for (const k of Object.keys(relaxationTarget.sharedConstraints)) {
          if (!sharedVars.has(k)) {
            sharedVars.set(k, []);
          }
          sharedVars.get(k)?.push(...costComponents);
        }
      }

      collectVars(relaxationTarget.constraints, interactionMap, weightMap, costComponents, vars);

      obbs.push(...costComponents);
    }
  }

  for (const [constraintKey, costComponents] of sharedVars) {
    const sharedVariable = new OBB2GradientDescentSharedVariable(
      constraintKey,
      costComponents,
      interactionMap,
      weightMap,
    );
    vars.push(sharedVariable);
  }

  // Get obstacles. A content template instance can span several categories, so the obstacles are
  // gathered up front and grouped across categories before their cost components are built.
  const obstacleGeometry: TaggedPlanGeometry[] = [];
  for (const obstacleTag of obstacleCategories) {
    obstacleGeometry.push(...(await source.getPlanGeometry(obstacleTag)));
  }

  const obstacles = constructObstaclesWithContentTemplateInstanceObbs(obstacleGeometry, textScale / viewScale);
  for (const obstacle of obstacles) {
    collectCostComponents(obstacle, textScale, viewScale, viewRotationRads, relaxedGroups, true, obbs);
  }

  let distance = 0;
  const intermediateStatuses: GradientDescentIntermediateStatus[] = [];
  const gradientDescentSpace = new OBB2GradientSpace(obbs, vars);

  const addIntermediateStatuses = (obbs: OBB2GradientDescentCostComponent[]) => {
    for (const obb of obbs) {
      // if (debugObstacleCategories[aabb.tag] || debugTargetCategories[aabb.tag]) {
      const subCostComponents = obb.getCurrentSubCostComponents();
      intermediateStatuses.push({ obbs: subCostComponents.map((o) => o.obb), color: obb.color, entity: obb.geometry });
      // }
    }
  };
  // add the initial state to the intermediate statuses
  addIntermediateStatuses(obbs);

  gradientDescent(gradientDescentSpace, iterationStep, (_gradientMagnitude) => {
    addIntermediateStatuses(obbs);
    distance += iterationStep;
    return distance < maxIterationDistance /* && gradientMagnitude > 0.0001*/;
  });

  return { intermediateStatuses, obbs };
}

export function constructGeometryWithLazyGroupRelaxationObbs(
  relaxationTargetTag: SheetGeometryCategory,
  scalingAmount: number,
  relaxationTargets: readonly TaggedPlanGeometry[],
) {
  if (!hasLazyRelaxationObstacleObbs(relaxationTargetTag)) {
    return relaxationTargets;
  }

  const obb2ByGroupId = relaxationObb2ByGroupId(relaxationTargets, scalingAmount, (geometry) => geometry.groupId);

  return relaxationTargets.map((geometry) => {
    if (geometry.groupId && obb2ByGroupId[geometry.groupId]) {
      return { ...geometry, relaxationObstacleOBBs: [obb2ByGroupId[geometry.groupId]] };
    }
    return geometry;
  });
}

/**
 * Unions the bounding boxes of every geometry sharing a relaxation group key. Text is scaled to
 * view space first, so the box accounts for the font size the geometry will actually be drawn at.
 */
function relaxationObb2ByGroupId(
  relaxationTargets: readonly TaggedPlanGeometry[],
  scalingAmount: number,
  groupId: (geometry: TaggedPlanGeometry) => string | undefined,
): Record<string, OBB2> {
  const geometryByGroupId: Record<string, TaggedPlanGeometry[]> = {};

  for (const geometry of relaxationTargets) {
    const id = groupId(geometry);
    if (!id) {
      continue;
    }

    (geometryByGroupId[id] ||= []).push(geometry);
  }

  const obb2ByGroupId: Record<string, OBB2> = {};

  for (const groupId in geometryByGroupId) {
    const aabbs: AABB2[] = [];
    for (const geometry of geometryByGroupId[groupId]) {
      const scaledGeometry: TaggedPlanGeometry =
        geometry.type === SheetGeometryType.TEXT ? sheetGeometryScaleStyle(geometry, scalingAmount) : geometry;
      const aabb = sheetGeometryAABB2(scaledGeometry);
      if (aabb) {
        aabbs.push(aabb);
      }
    }

    if (aabbs.length > 0) {
      const unionOBB = obb2FromAABB2(aabb2UnionAll(aabbs));
      obb2ByGroupId[groupId] = unionOBB;
    }
  }

  return obb2ByGroupId;
}

/**
 * Annotation drawing template instances are flattened into individual a loose collection of notes,
 * linework, and details that carry the same content template instance handle. Relaxing against the
 * whole template, rather than each piece.
 */
export function constructObstaclesWithContentTemplateInstanceObbs(
  obstacles: readonly TaggedPlanGeometry[],
  scalingAmount: number,
): readonly TaggedPlanGeometry[] {
  const obb2ByInstanceId = relaxationObb2ByGroupId(obstacles, scalingAmount, contentTemplateInstanceObstacleKey);

  const representativeByInstanceId: Record<string, TaggedPlanGeometry> = {};
  for (const geometry of obstacles) {
    const instanceId = contentTemplateInstanceObstacleKey(geometry);
    if (!instanceId || !obb2ByInstanceId[instanceId]) {
      continue;
    }

    const representative = representativeByInstanceId[instanceId];
    if (
      representative === undefined ||
      contentTemplateInstanceObstaclePriority(geometry) < contentTemplateInstanceObstaclePriority(representative)
    ) {
      representativeByInstanceId[instanceId] = geometry;
    }
  }

  return obstacles.flatMap((geometry) => {
    const instanceId = contentTemplateInstanceObstacleKey(geometry);
    if (!instanceId || !obb2ByInstanceId[instanceId]) {
      return [geometry];
    }

    // The whole instance is represented by a single obstacle, so drop the remaining pieces
    // rather than repeating the same box once per element.
    if (representativeByInstanceId[instanceId] !== geometry) {
      return [];
    }

    return [{ ...geometry, relaxationObstacleOBBs: [obb2ByInstanceId[instanceId]] }];
  });
}

/**
 * Categories whose geometry is grouped into one obstacle per hosting content template instance,
 * ordered by which piece gets to represent its group. The representative's category is the tag the
 * relaxer looks up in the interaction map, so it has to come from an explicit ordering rather than
 * from whichever category the obstacles happened to be collected in first.
 *
 * Dimensions are deliberately excluded: their linework and witness lines are relaxed as dimension
 * groups and must keep their own bounding boxes.
 */
const CONTENT_TEMPLATE_INSTANCE_OBSTACLE_CATEGORIES: readonly SheetGeometryCategory[] = [
  SheetGeometryCategory.ANNOT_DETAIL,
  SheetGeometryCategory.ANNOT_LINEWORK,
  SheetGeometryCategory.ANNOT_NOTE,
];

function contentTemplateInstanceObstaclePriority(geometry: TaggedPlanGeometry): number {
  return CONTENT_TEMPLATE_INSTANCE_OBSTACLE_CATEGORIES.indexOf(geometry.category);
}

function contentTemplateInstanceObstacleKey(geometry: TaggedPlanGeometry): string | undefined {
  if (contentTemplateInstanceObstaclePriority(geometry) === -1) {
    return undefined;
  }

  return geometry.handle?.type === "CONTENT_TEMPLATE_INSTANCE" ? geometry.handle.id : undefined;
}

export interface GradientDescentIntermediateStatus {
  obbs: OBB2[];
  color: string;
  entity: TaggedPlanGeometry;
}

interface GradientDescentVector<APROXIMATION> {
  numVars(): number;
  numCostComponents(): number;

  // updates proxy search data structure
  updateProxySearchDataStructure(): void;

  // Calculates the ith component of the cost function
  calculateCost(c: GradientDescentCostComponent): number;

  costComponents(approximation: APROXIMATION): Iterable<GradientDescentCostComponent>;
  variables(): Iterable<GradientDescentVariable<APROXIMATION>>;
}

interface GradientDescentVariable<APROXIMATION> {
  // differentiates the `c` component of the cost function with respect to this variable
  calculateGradientDimension(c: GradientDescentCostComponent): number;

  // adds a value to delta (for debugging only)
  addDelta(delta: number): void;
  // Saves the delta to be updated with
  saveDelta(delta: number): void;
  // Actually update by adding the delta
  update(): void;
  // Gets the approximation object that is used to speed-up the cost function
  getApproximation(): APROXIMATION;
  // Gets the weight of the variable
  getWeight(): number;
}

interface GradientDescentCostComponent {}

function gradientDescent<T>(
  start: GradientDescentVector<T>,
  speed: number = 0.01,
  goWhile: (_gradientMagnitude: number) => boolean = (gradientMagnitude) => gradientMagnitude < 0.0001,
) {
  const p = start;
  let gradientMagnitude = 0;
  do {
    p.updateProxySearchDataStructure();

    gradientMagnitude = 0;
    for (const v of p.variables()) {
      let delta = 0;
      const costComponents = p.costComponents(v.getApproximation());
      for (const c of costComponents) {
        const gradientWithRespectToVar = v.calculateGradientDimension(c);
        delta -= gradientWithRespectToVar;
      }

      const weight = v.getWeight();
      const updateFactor = Math.min(Math.max(weight > 0 ? 1 / weight : 0, 0), 1);
      v.saveDelta(delta * speed * updateFactor);

      gradientMagnitude += delta * delta;
    }
    for (const v of p.variables()) {
      v.update();
    }
  } while (goWhile(gradientMagnitude));
}

// function gradientDescentDebug(
//   start: GradientDescentVector,
//   speed: number = 0.01,
//   goWhile: (_gradientMagnitude: number) => boolean = (gradientMagnitude) => gradientMagnitude < 0.0001,
// ) {
//   const p = start;
//   let gradientMagnitude = 0;
//   do {
//     gradientMagnitude = 0;
//     for (const c of p.costComponents()) {
//       const deltas = [];
//       for (const v of p.variables()) {
//         const gradientWithRespectToVar = v.calculateGradientDimension(c);
//         deltas.push({ v, delta: -gradientWithRespectToVar });
//       }
//       for (const { v, delta } of deltas) {
//         v.addDelta(delta * speed);
//         gradientMagnitude += delta * delta;
//       }
//     }
//     for (const v of p.variables()) {
//       v.update();
//       v.saveDelta(0);
//     }
//   } while (goWhile(gradientMagnitude));
// }

const BB_PADDING = sheetInchesNew(1 / 4);

interface OBB2GradientDescentSubCostComponent {
  readonly obb: OBB2;
  readonly tag: SheetGeometryCategory;
  readonly constraintDirection?: Vec2;
}

class OBB2GradientDescentCostComponent implements GradientDescentCostComponent {
  public shiftX = 0;
  public shiftY = 0;
  constructor(
    public readonly initialOBB2: OBB2,
    public readonly color: string,
    public readonly geometry: TaggedPlanGeometry,
    public readonly initialSubCostComponents: OBB2GradientDescentSubCostComponent[] = [],
  ) {}
  getApproximation() {
    const subCostComponents = this.getCurrentSubCostComponents();

    if (!(subCostComponents.length > 0)) {
      console.assert(false, "OBB2GradientDescentCostComponent must have at least one main cost component");
    }

    let aabb2 = aabb2FromOBB2(subCostComponents[0].obb);

    for (let i = 1, l = subCostComponents.length; i < l; i++) {
      const subAABB2 = aabb2FromOBB2(subCostComponents[i].obb);
      aabb2 = aabb2Union(aabb2, subAABB2);
    }

    return aabb2;
  }

  getCurrentSubCostComponents(): OBB2GradientDescentSubCostComponent[] {
    return [
      {
        obb: obb2Move(obb2Grow(obb2Copy(this.initialOBB2), BB_PADDING, BB_PADDING), this.shiftX, this.shiftY),
        tag: this.geometry.category,
      },
      ...this.initialSubCostComponents.map((subCostComponent) => {
        let subOBB2ShiftX = this.shiftX;
        let subOBB2ShiftY = this.shiftY;

        if (subCostComponent.constraintDirection) {
          // project the shift onto the constraint direction
          const proj = vec2Dot(
            subCostComponent.constraintDirection[0],
            subCostComponent.constraintDirection[1],
            this.shiftX,
            this.shiftY,
          );

          subOBB2ShiftX = proj * subCostComponent.constraintDirection[0];
          subOBB2ShiftY = proj * subCostComponent.constraintDirection[1];
        }

        return {
          obb: obb2Move(obb2Grow(obb2Copy(subCostComponent.obb), BB_PADDING, BB_PADDING), subOBB2ShiftX, subOBB2ShiftY),
          tag: subCostComponent.tag,
        };
      }),
    ];
  }

  hasSameGroup(c: OBB2GradientDescentCostComponent): boolean {
    return !!this.geometry.groupId && this.geometry.groupId === c.geometry.groupId;
  }

  calculateCost(space: OBB2GradientSpace): number {
    let cost = 0;
    const costComponents = space.costComponents(this.getApproximation());
    const subCostComponents = this.getCurrentSubCostComponents();

    for (const c of costComponents) {
      const obbCostComponent = c as OBB2GradientDescentCostComponent;
      if (c === this || this.hasSameGroup(obbCostComponent)) {
        continue;
      }

      const otherSubCostComponents = obbCostComponent.getCurrentSubCostComponents();

      for (const subCostComponent of subCostComponents) {
        for (const otherSubCostComponent of otherSubCostComponents) {
          const intersection = obb2CollisionDetect(subCostComponent.obb, otherSubCostComponent.obb);

          if (intersection) {
            cost += intersection.intersectionArea;
          }
        }
      }
    }

    return cost;
  }
}

class OBB2GradientDescentSharedVariable implements GradientDescentVariable<AABB2> {
  // Conceptually, this is a single variable. However, for the sake of code reuse
  // we "spread" it out into multiple variables.
  // CAVEAT: This works only if the constraints are compatible. That is,
  // if their direction is the same, their weight is the same, etc.
  // The `collectVars` call doesn't guarantee this. Therefore the input to the gradient descent
  // must ensure this precondition..
  // TODO: use a single variable that can update multiple cost components; check the shared constraints
  // for compatibility, and aggregate them. E.g. if one constraint restricts Y position to be between
  // 10 and 30, and another restricts Y position to be between 20 and 40, then the variable should
  // restrict Y position to be between 20 and 30.
  public variables: GradientDescentVariable<AABB2>[] = [];

  constructor(
    public readonly constraintKey: string,
    public readonly costComponents: OBB2GradientDescentCostComponent[],
    interactionMap: SheetGeometryCategoryTable<RelaxationObstacle[]>,
    weightMap: SheetGeometryCategoryTable<number>,
  ) {
    this.costComponents.forEach((c) => {
      if (c.geometry.sharedConstraints?.[this.constraintKey]) {
        const constraintValues = c.geometry.sharedConstraints[this.constraintKey];
        if (constraintValues === GRADIENT_DESCENT_NO_CONSTRAINT) {
          return; // this geometry is in the group, but is unbounded
        }

        collectVars(constraintValues, interactionMap, weightMap, [c], this.variables);
      }
    });
  }

  calculateGradientDimension(c: GradientDescentCostComponent): number {
    let gradient = 0;

    for (const variable of this.variables) {
      gradient += variable.calculateGradientDimension(c);
    }

    return gradient;
  }
  addDelta(delta: number): void {
    // normalize the delta by the number of variables
    // (this avoid applying the same delta several times, one for each variable)
    const deltaPerVariable = delta / Math.max(1, this.variables.length);
    for (const variable of this.variables) {
      variable.addDelta(deltaPerVariable);
    }
  }
  saveDelta(delta: number): void {
    // normalize the delta by the number of variables
    // (this avoid applying the same delta several times, one for each variable)
    const deltaPerVariable = delta / Math.max(1, this.variables.length);
    for (const variable of this.variables) {
      variable.saveDelta(deltaPerVariable);
    }
  }
  update(): void {
    for (const variable of this.variables) {
      variable.update();
    }
  }
  getApproximation(): AABB2 {
    return aabb2UnionAll(this.variables.map((v) => v.getApproximation()));
  }
  getWeight(): number {
    if (this.variables.length === 0) {
      return 0;
    }
    const w = this.variables.map((v) => v.getWeight()).reduce((a, b) => a + b, 0);
    return w / this.variables.length;
  }
}

class OBB2GradientDescentLimitedXVariable implements GradientDescentVariable<AABB2> {
  private delta = 0;
  constructor(
    public readonly costComponent: OBB2GradientDescentCostComponent,
    private readonly weight: number,
    private readonly interactionMap: SheetGeometryCategoryTable<RelaxationObstacle[]>,
    private readonly limitMin: number,
    private readonly limitMax: number,
  ) {}

  getApproximation(): AABB2 {
    return this.costComponent.getApproximation();
  }

  getWeight(): number {
    return this.weight;
  }

  addDelta(delta: number): void {
    this.delta += delta;
  }

  saveDelta(d: number): void {
    this.delta = d;
  }

  update(): void {
    if (this.delta === 0) {
      return;
    }

    const initialAABB2 = aabb2FromOBB2(this.costComponent.initialOBB2);

    this.costComponent.shiftX += this.delta;
    if (initialAABB2.minX + this.costComponent.shiftX < this.limitMin) {
      this.costComponent.shiftX = this.limitMin - initialAABB2.minX;
      return;
    } else if (initialAABB2.maxX + this.costComponent.shiftX > this.limitMax) {
      this.costComponent.shiftX = this.limitMax - initialAABB2.maxX;
    }
  }

  calculateGradientDimension(c: OBB2GradientDescentCostComponent): number {
    if (this.costComponent === c || this.costComponent.hasSameGroup(c)) {
      return 0;
    }

    const subCostComponents = this.costComponent.getCurrentSubCostComponents();
    const otherSubCostComponents = c.getCurrentSubCostComponents();

    let gradient = 0;
    for (const subCostComponent of subCostComponents) {
      const { obb: thisOBB, tag: thisTag } = subCostComponent;

      const thisAABB2 = aabb2FromOBB2(thisOBB);

      for (const otherSubCostComponent of otherSubCostComponents) {
        const { obb: otherOBB, tag: otherTag } = otherSubCostComponent;

        const cIndex = this.interactionMap[thisTag]?.findIndex((val) => val.category === otherTag);
        if (cIndex === undefined || cIndex === -1) {
          continue;
        }

        const obstacle = this.interactionMap[thisTag]?.[cIndex];
        if (!obstacle?.selector || !evalSheetGeometrySelector(this.costComponent.geometry, obstacle.selector)) {
          continue;
        }

        const intersection = obb2CollisionDetect(thisOBB, otherOBB);
        if (!intersection) {
          continue;
        }

        const { intersectionArea, intersectionCenterX } = intersection;

        const movedOBB1 = obb2Move(obb2Copy(thisOBB), SAMPLING_OFFSET, 0);
        const movedOBB2 = obb2Move(obb2Copy(thisOBB), -SAMPLING_OFFSET, 0);

        const movedIntersection1 = obb2CollisionDetect(movedOBB1, otherOBB);
        const movedIntersection2 = obb2CollisionDetect(movedOBB2, otherOBB);

        const movedIntersection1Area = movedIntersection1?.intersectionArea ?? 0;
        const movedIntersection2Area = movedIntersection2?.intersectionArea ?? 0;

        const areaDiff = movedIntersection1Area - movedIntersection2Area;

        if (Math.abs(areaDiff) < DIST_EPSILON_SQUARED) {
          // if the areas are the same, we need to move the center apart from the intersection center
          const otherAABB2 = aabb2FromOBB2(otherOBB);

          let [cx] = aabb2Center(thisAABB2);
          let ocx = intersectionCenterX;

          const xOverlap = aabb2XOverlap(thisAABB2, otherAABB2);
          const yOverlap = aabb2YOverlap(thisAABB2, otherAABB2);
          if (ocx === cx) {
            // if the intersection center is the same as the obb center, we need to move the centers apart
            [ocx] = aabb2Center(otherAABB2);
            if (ocx === cx) {
              cx += Math.random() * 0.0001;
            }
          }

          gradient += yOverlap * Math.sign(xOverlap) * Math.sign(ocx - cx);
        } else {
          gradient += Math.sqrt(intersectionArea) * Math.sign(areaDiff);
        }
      }
    }

    return gradient;
  }
}

class OBB2GradientDescentLimitedYVariable implements GradientDescentVariable<AABB2> {
  private delta = 0;
  constructor(
    public readonly costComponent: OBB2GradientDescentCostComponent,
    private readonly weight: number,
    private readonly interactionMap: SheetGeometryCategoryTable<RelaxationObstacle[]>,
    private readonly limitMin: number,
    private readonly limitMax: number,
  ) {}

  getApproximation(): AABB2 {
    return this.costComponent.getApproximation();
  }

  getWeight(): number {
    return this.weight;
  }

  addDelta(delta: number): void {
    this.delta += delta;
  }

  saveDelta(d: number): void {
    this.delta = d;
  }

  update(): void {
    if (this.delta === 0) {
      return;
    }

    const initialAABB2 = aabb2FromOBB2(this.costComponent.initialOBB2);

    this.costComponent.shiftY += this.delta;
    if (initialAABB2.minY + this.costComponent.shiftY < this.limitMin) {
      this.costComponent.shiftY = this.limitMin - initialAABB2.minY;
      return;
    } else if (initialAABB2.maxY + this.costComponent.shiftY > this.limitMax) {
      this.costComponent.shiftY = this.limitMax - initialAABB2.maxY;
    }
  }

  calculateGradientDimension(c: OBB2GradientDescentCostComponent): number {
    if (this.costComponent === c || this.costComponent.hasSameGroup(c)) {
      return 0;
    }

    const subCostComponents = this.costComponent.getCurrentSubCostComponents();
    const otherSubCostComponents = c.getCurrentSubCostComponents();

    let gradient = 0;
    for (const subCostComponent of subCostComponents) {
      const { obb: thisOBB, tag: thisTag } = subCostComponent;

      const thisAABB2 = aabb2FromOBB2(thisOBB);

      for (const otherSubCostComponent of otherSubCostComponents) {
        const { obb: otherOBB, tag: otherTag } = otherSubCostComponent;

        const cIndex = this.interactionMap[thisTag]?.findIndex((val) => val.category === otherTag);
        if (cIndex === undefined || cIndex === -1) {
          continue;
        }

        const obstacle = this.interactionMap[thisTag]?.[cIndex];
        if (!obstacle?.selector || !evalSheetGeometrySelector(this.costComponent.geometry, obstacle.selector)) {
          continue;
        }

        const intersection = obb2CollisionDetect(thisOBB, otherOBB);
        if (!intersection) {
          continue;
        }

        const { intersectionArea, intersectionCenterY } = intersection;

        const movedOBB1 = obb2Move(obb2Copy(thisOBB), 0, SAMPLING_OFFSET);
        const movedOBB2 = obb2Move(obb2Copy(thisOBB), 0, -SAMPLING_OFFSET);

        const movedIntersection1 = obb2CollisionDetect(movedOBB1, otherOBB);
        const movedIntersection2 = obb2CollisionDetect(movedOBB2, otherOBB);

        const movedIntersection1Area = movedIntersection1?.intersectionArea ?? 0;
        const movedIntersection2Area = movedIntersection2?.intersectionArea ?? 0;

        const areaDiff = movedIntersection1Area - movedIntersection2Area;

        if (Math.abs(areaDiff) < DIST_EPSILON_SQUARED) {
          // if the areas are the same, we need to move the center apart from the intersection center
          const otherAABB2 = aabb2FromOBB2(otherOBB);

          let [, cy] = aabb2Center(thisAABB2);
          let ocy = intersectionCenterY;

          const xOverlap = aabb2XOverlap(thisAABB2, otherAABB2);
          const yOverlap = aabb2YOverlap(thisAABB2, otherAABB2);
          if (ocy === cy) {
            // if the intersection center is the same as the obb center, we need to move the centers apart
            [, ocy] = aabb2Center(otherAABB2);
            if (ocy === cy) {
              cy += Math.random() * 0.0001;
            }
          }

          gradient += xOverlap * Math.sign(yOverlap) * Math.sign(ocy - cy);
        } else {
          gradient += Math.sqrt(intersectionArea) * Math.sign(areaDiff);
        }
      }
    }

    return gradient;
  }
}

class OBB2GradientDescentLinearLimitedVariable implements GradientDescentVariable<AABB2> {
  private delta = 0;
  private direction: number[] = [0, 0];
  private origin: number[] = [0, 0];
  private length: number = 0;
  constructor(
    public readonly costComponent: OBB2GradientDescentCostComponent,
    private readonly weight: number,
    private readonly interactionMap: SheetGeometryCategoryTable<RelaxationObstacle[]>,
    limitSegment: Line2Segment,
  ) {
    const [dx, dy] = vec2Sub(limitSegment[2], limitSegment[3], limitSegment[0], limitSegment[1]);
    const length = vec2Len(dx, dy);

    this.direction = length > DIST_EPSILON ? vec2Copy(vec2Normalize(dx, dy)) : [0, 0];
    this.origin = [limitSegment[0], limitSegment[1]];
    this.length = length;
  }

  getApproximation(): AABB2 {
    return this.costComponent.getApproximation();
  }

  getWeight(): number {
    return this.weight;
  }

  addDelta(delta: number): void {
    this.delta += delta;
  }

  saveDelta(d: number): void {
    this.delta = d;
  }

  isInsideLimits(obb: OBB2): boolean {
    const { centerX, centerY } = obb;

    // project the obb center onto the constraint
    const t = ray2DotProject(this.origin[0], this.origin[1], this.direction[0], this.direction[1], centerX, centerY);

    // check if the distance is inside the constraint
    return t > -DIST_EPSILON && t < this.length + DIST_EPSILON;
  }

  update(): void {
    if (this.delta === 0 || this.length < DIST_EPSILON) {
      return;
    }

    const subCostComponents = this.costComponent.getCurrentSubCostComponents();
    if (!(subCostComponents.length > 0)) {
      console.assert(false, "OBB2GradientDescentLinearLimitedVariable must have at least one main cost component");
    }

    const thisOBB2 = subCostComponents[0].obb;
    if (!this.isInsideLimits(thisOBB2)) {
      console.error("OBB2GradientDescentLinearLimitedVariable pre-check failed!");
    }

    // project the obb center onto the constraint
    const t = ray2DotProject(
      this.origin[0],
      this.origin[1],
      this.direction[0],
      this.direction[1],
      thisOBB2.centerX,
      thisOBB2.centerY,
    );

    const toHitMaxFactor = Math.min(Math.max(0, this.length - t), this.length);
    const toHitMinFactor = -Math.min(Math.max(0, t), this.length);

    const sign = Math.sign(this.delta);
    let factor = 0;
    if (sign > 0) {
      factor = Math.min(this.delta, toHitMaxFactor);
    } else if (sign < 0) {
      factor = Math.max(this.delta, toHitMinFactor);
    }

    // move the component if we have a valid factor
    if (factor !== Number.MAX_VALUE) {
      this.costComponent.shiftX += factor * this.direction[0];
      this.costComponent.shiftY += factor * this.direction[1];

      const updatedSubCostComponents = this.costComponent.getCurrentSubCostComponents();
      if (!(updatedSubCostComponents.length > 0)) {
        console.assert(false, "OBB2GradientDescentLinearLimitedVariable must have at least one main cost component");
      }

      if (!this.isInsideLimits(updatedSubCostComponents[0].obb)) {
        console.error("OBB2GradientDescentLinearLimitedVariable post-check failed!");
      }
    }
  }

  calculateGradientDimension(c: OBB2GradientDescentCostComponent): number {
    if (this.costComponent === c || this.costComponent.hasSameGroup(c)) {
      return 0;
    }

    const subCostComponents = this.costComponent.getCurrentSubCostComponents();
    const otherSubCostComponents = c.getCurrentSubCostComponents();

    let gradient = 0;
    for (const subCostComponent of subCostComponents) {
      const { obb: thisOBB, tag: thisTag } = subCostComponent;
      const { centerX: cx, centerY: cy } = thisOBB;

      for (const otherSubCostComponent of otherSubCostComponents) {
        const { obb: otherOBB, tag: otherTag } = otherSubCostComponent;

        const cIndex = this.interactionMap[thisTag]?.findIndex((val) => val.category === otherTag);
        if (cIndex === undefined || cIndex === -1) {
          continue;
        }

        const obstacle = this.interactionMap[thisTag]?.[cIndex];
        if (!obstacle?.selector || !evalSheetGeometrySelector(this.costComponent.geometry, obstacle.selector)) {
          continue;
        }

        const intersection = obb2CollisionDetect(thisOBB, otherOBB);
        if (!intersection) {
          continue;
        }

        const { intersectionArea, intersectionCenterX, intersectionCenterY } = intersection;

        // look for the direction where the intersection area gets smaller (or disappears)
        const movedOBB1 = obb2Move(
          obb2Copy(thisOBB),
          this.direction[0] * SAMPLING_OFFSET,
          this.direction[1] * SAMPLING_OFFSET,
        );
        const movedOBB2 = obb2Move(
          obb2Copy(thisOBB),
          -this.direction[0] * SAMPLING_OFFSET,
          -this.direction[1] * SAMPLING_OFFSET,
        );

        const movedIntersection1 = obb2CollisionDetect(movedOBB1, otherOBB);
        const movedIntersection2 = obb2CollisionDetect(movedOBB2, otherOBB);

        let projFactor = 0;

        const movedIntersection1Area = movedIntersection1?.intersectionArea ?? 0;
        const movedIntersection2Area = movedIntersection2?.intersectionArea ?? 0;

        const areaDiff = movedIntersection1Area - movedIntersection2Area;

        let ocx = intersectionCenterX;
        let ocy = intersectionCenterY;

        if (Math.abs(areaDiff) < DIST_EPSILON_SQUARED) {
          // if the areas are the same, we need to move the center apart from the intersection center
          let [dx, dy] = vec2Sub(ocx, ocy, cx, cy);
          projFactor = vec2ProjectScaleFactor(dx, dy, this.direction[0], this.direction[1]);

          // if the intersection center is the same as the obb center, we need to move the centers apart
          if (projFactor === 0) {
            const otherAABB2 = aabb2FromOBB2(otherOBB);
            [ocx, ocy] = aabb2Center(otherAABB2);
            [dx, dy] = vec2Sub(ocx, ocy, cx, cy);
            projFactor = vec2ProjectScaleFactor(dx, dy, this.direction[0], this.direction[1]);
          }

          if (projFactor === 0) {
            projFactor += Math.random() * 0.0001;
          }
        } else {
          // if the areas are different, we need to move the centers towards the smaller area
          projFactor = Math.sign(areaDiff);
        }

        gradient += Math.sqrt(intersectionArea) * Math.sign(projFactor);
      }
    }

    return gradient;
  }
}

interface CostComponentRTreeBox extends AABB2 {
  readonly costComponent: OBB2GradientDescentCostComponent;
}

class OBB2GradientSpace implements GradientDescentVector<AABB2> {
  constructor(
    private readonly obbs: OBB2GradientDescentCostComponent[],
    private readonly vars: GradientDescentVariable<AABB2>[],
  ) {}

  private rtree: RBush<CostComponentRTreeBox> = new RBush<CostComponentRTreeBox>();

  updateProxySearchDataStructure(): void {
    this.rtree.clear();
    this.rtree.load(this.obbs.map((c) => ({ ...c.getApproximation(), costComponent: c })));
  }
  numVars(): number {
    return this.vars.length;
  }
  numCostComponents(): number {
    return this.obbs.length;
  }
  costComponents(approximation: AABB2): Iterable<GradientDescentCostComponent> {
    return this.rtree.search(approximation).map((b) => b.costComponent);
  }
  variables(): Iterable<GradientDescentVariable<AABB2>> {
    return this.vars;
  }

  calculateCost(c: GradientDescentCostComponent): number {
    return (c as OBB2GradientDescentCostComponent).calculateCost(this);
  }
}
